import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

// Google AdSense Configuration
const ADSENSE_CLIENT = "ca-pub-9368217363569697";
const AD_SLOTS = {
  square: "4466231726",    // 300x300
  banner: "1047926464",    // Barra horizontal
};

// Check if we're in production (real domain)
const isProduction = typeof window !== 'undefined' && 
  (window.location.hostname === 'choluteca.net' || 
   window.location.hostname === 'www.choluteca.net');

// Load AdSense script once
let adsenseLoaded = false;
const loadAdsenseScript = () => {
  if (adsenseLoaded || typeof window === 'undefined') return;
  if (!isProduction) return; // Don't load in development
  
  const script = document.createElement('script');
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
  script.async = true;
  script.crossOrigin = "anonymous";
  document.head.appendChild(script);
  adsenseLoaded = true;
};

const adSizes = {
  banner: "h-[90px] md:h-[90px]",
  rectangle: "h-[250px]",
  skyscraper: "h-[600px] w-[160px]",
  largeRectangle: "h-[280px]",
  mobileBanner: "h-[50px]",
  inArticle: "h-[250px]",
};

// Real AdSense component
function RealAdSense({ slot, className }) {
  const adRef = useRef(null);
  const pushed = useRef(false);

  useEffect(() => {
    loadAdsenseScript();
    
    if (!pushed.current && isProduction) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushed.current = true;
      } catch (e) {
        console.error('AdSense error:', e);
      }
    }
  }, []);

  if (!isProduction) {
    return null; // Will show placeholder instead
  }

  return (
    <ins
      ref={adRef}
      className={cn("adsbygoogle", className)}
      style={{ display: 'block' }}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={slot}
      data-ad-format="auto"
      data-full-width-responsive="true"
    />
  );
}

// Placeholder for development
function DevPlaceholder({ size, label }) {
  return (
    <div 
      className={cn(
        "relative bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl border border-dashed border-gray-300 flex items-center justify-center overflow-hidden",
        adSizes[size]
      )}
    >
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      <div className="text-center z-10">
        <p className="text-xs uppercase tracking-wider text-gray-400 font-medium">{label}</p>
        <p className="text-[10px] text-gray-300 mt-1">Google Ads (Dev Mode)</p>
      </div>
    </div>
  );
}

export function AdPlaceholder({ 
  size = "rectangle", 
  className = "",
  label = "Publicidad",
  testId = "ad-placeholder"
}) {
  const slot = size === "banner" ? AD_SLOTS.banner : AD_SLOTS.square;

  return (
    <div 
      className={cn(adSizes[size], className)}
      data-testid={testId}
    >
      {isProduction ? (
        <RealAdSense slot={slot} className="w-full h-full" />
      ) : (
        <DevPlaceholder size={size} label={label} />
      )}
    </div>
  );
}

// Component for horizontal banner across full width
export function AdBanner({ className = "" }) {
  return (
    <div className={cn("w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8", className)}>
      {isProduction ? (
        <RealAdSense slot={AD_SLOTS.banner} className="w-full" />
      ) : (
        <DevPlaceholder size="banner" label="Publicidad - Barra" />
      )}
    </div>
  );
}

// Sidebar ad component (square)
export function AdSidebar({ className = "" }) {
  return (
    <div className={cn("space-y-4", className)}>
      {isProduction ? (
        <RealAdSense slot={AD_SLOTS.square} className="w-full max-w-[300px] mx-auto" />
      ) : (
        <DevPlaceholder size="rectangle" label="Publicidad - 300x300" />
      )}
    </div>
  );
}

// Export AdSense config for use elsewhere
export { ADSENSE_CLIENT, AD_SLOTS, isProduction, loadAdsenseScript };
